import { buttonStyle } from 'components/buttons/buttonStyle';
import { LabelCard } from 'components/cards/LabelCard';
import { ErrorOutline } from 'components/icons';
import { Alert } from 'components/ui/alert';
import { Button } from 'components/ui/button';
import MoneyDisplay from 'components/util/MoneyDisplay';
import { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import { useGetPortfolioSummaryQuery } from 'store/api/portfolio';
import { buildPortfolioScopeParams, PortfolioScopeState } from 'types/portfolio';
import { cn } from 'utils/cn';
import { buildActiveClientsTooltip, buildCardOwnershipLabel, buildTotalClientsTooltip } from './portfolioScope';

// Each grid cell stretches to the tallest row item; the cell then stretches the LabelCard inside it
// to fill it, so all four cards end up the same height whatever their content.
const CARD_CELL = 'flex [&>*]:flex-1';

// Divides by Math.abs(previous) so a negative prior baseline (e.g. credit notes outweighing
// billings last year) doesn't flip the percentage's sign — a turnaround from a loss year to
// positive revenue then reads as a positive percentage, matching the table-cell helper in
// MyPortfolioTable.tsx. Zero baseline is suppressed (percentage off zero is undefined).
const toPercentDeltaSign = (current: number | null | undefined, previous: number | null | undefined) => {
    if (previous == null || previous === 0) return null;
    const pct = (((current ?? 0) - previous) / Math.abs(previous)) * 100;
    return Math.round(pct);
};

// Inline trailing piece rendered next to the big card value: "(<message>)" in title-size
// (1rem), normal weight. Bracket text is secondary by default; tone="error" turns the whole
// bracket red (used for overdue). Bold weight is reserved for coloured numerical tokens
// dropped in via children — see SignedDelta and the overdue amount wrapper.
const InlineSecondary = ({ children, tone = 'secondary' }: { children: ReactNode; tone?: 'secondary' | 'error' }) => (
    <span
        className={cn(
            'ml-1.5 align-baseline text-base leading-normal font-normal',
            tone === 'error' ? 'text-error' : 'text-fg-secondary'
        )}
    >
        ({children})
    </span>
);

const BoldNumeric = ({ children, className }: { children: ReactNode; className?: string }) => (
    <span className={cn('font-semibold', className)}>{children}</span>
);

const SignedDelta = ({ value, suffix = '' }: { value: number; suffix?: string }) => {
    const isPositive = value >= 0;
    return (
        <BoldNumeric className={isPositive ? 'text-primary' : 'text-error'}>
            {`${isPositive ? '+' : ''}${value}${suffix}`}
        </BoldNumeric>
    );
};

const MyPortfolioSummaryCards = ({ scope }: { scope: PortfolioScopeState }) => {
    const { data, isFetching, isError, refetch } = useGetPortfolioSummaryQuery(buildPortfolioScopeParams(scope));

    if (isError) {
        return (
            <Alert className="bg-error-lighter text-fg flex items-center gap-2 rounded-lg border-transparent py-2 pr-4 pl-3 font-semibold">
                {/* A wrapper, not a bare svg child: the kit alert lays a direct svg out as a grid column. */}
                <span className="flex w-6 shrink-0 justify-center [&>svg]:size-4">
                    <ErrorOutline className="text-error" />
                </span>
                <div className="min-w-0">
                    <FormattedMessage id="portfolio.cards.error" />
                </div>
                <Button
                    {...buttonStyle({ variant: 'text' }, 'text-inherit ml-auto')}
                    onClick={() => {
                        void refetch();
                    }}
                >
                    <FormattedMessage id="portfolio.cards.retry" />
                </Button>
            </Alert>
        );
    }

    const revenuePercent = toPercentDeltaSign(data?.totalRevenueYtd, data?.totalRevenueYtdM1);
    // Whose numbers these two cards sum, following the active scope. Null in firm scope, where the
    // heading renders with no parenthetical at all.
    const ownershipLabel = buildCardOwnershipLabel(scope);
    const ownershipValues = {
        ownership: ownershipLabel ? (
            <span key="ownership" className="text-fg-secondary">
                {ownershipLabel}
            </span>
        ) : null,
    };
    // The two count cards explain whose RP/RM relationships they count, so their tooltips follow the
    // scope too — including a firm variant, where the count is not restricted by ownership at all.
    const totalClientsTooltip = buildTotalClientsTooltip(scope);
    const activeClientsTooltip = buildActiveClientsTooltip(scope);

    return (
        // 2-up below the sm: breakpoint (768px), 4-up above.
        <div className="new:gap-(--card-gap) grid grid-cols-2 gap-4 sm:grid-cols-4">
            <div className={CARD_CELL}>
                <LabelCard
                    labelId="portfolio.cards.total-clients"
                    tooltipText={totalClientsTooltip?.id}
                    tooltipValues={totalClientsTooltip?.values}
                    fontSize="large"
                    content={
                        <>
                            {isFetching ? '—' : (data?.totalClientsAndLeads ?? 0)}
                            {data?.totalClientsAndLeadsLastQuarterDelta != null ? (
                                <InlineSecondary>
                                    <FormattedMessage
                                        id="portfolio.cards.lq-delta"
                                        values={{
                                            delta: (
                                                <SignedDelta
                                                    key="delta"
                                                    value={data.totalClientsAndLeadsLastQuarterDelta}
                                                />
                                            ),
                                        }}
                                    />
                                </InlineSecondary>
                            ) : null}
                        </>
                    }
                />
            </div>
            <div className={CARD_CELL}>
                <LabelCard
                    labelId="portfolio.cards.active-clients"
                    labelValues={{
                        window: (
                            <span key="window" className="text-fg-secondary">
                                <FormattedMessage id="portfolio.cards.active-clients-window" />
                            </span>
                        ),
                    }}
                    tooltipText={activeClientsTooltip?.id}
                    tooltipValues={activeClientsTooltip?.values}
                    content={isFetching ? '—' : (data?.activeClients ?? 0)}
                    fontSize="large"
                />
            </div>
            <div className={CARD_CELL}>
                <LabelCard
                    labelId="portfolio.cards.total-revenue-ytd"
                    labelValues={ownershipValues}
                    tooltipText="portfolio.cards.total-revenue-ytd-tooltip"
                    fontSize="large"
                    content={
                        <>
                            <MoneyDisplay amount={data?.totalRevenueYtd ?? 0} decimalScale={0} />
                            {revenuePercent !== null ? (
                                <InlineSecondary>
                                    <FormattedMessage
                                        id="portfolio.cards.ly-delta"
                                        values={{
                                            delta: <SignedDelta key="delta" value={revenuePercent} suffix="%" />,
                                        }}
                                    />
                                </InlineSecondary>
                            ) : null}
                        </>
                    }
                />
            </div>
            <div className={CARD_CELL}>
                <LabelCard
                    labelId="portfolio.cards.outstanding"
                    labelValues={ownershipValues}
                    tooltipText="portfolio.cards.outstanding-tooltip"
                    fontSize="large"
                    content={
                        <>
                            <MoneyDisplay amount={data?.outstandingBalance ?? 0} decimalScale={0} />
                            {(data?.overdueAmount ?? 0) > 0 ? (
                                <InlineSecondary tone="error">
                                    <FormattedMessage
                                        id="portfolio.cards.overdue"
                                        values={{
                                            amount: (
                                                <BoldNumeric key="amount">
                                                    <MoneyDisplay amount={data?.overdueAmount ?? 0} decimalScale={0} />
                                                </BoldNumeric>
                                            ),
                                        }}
                                    />
                                </InlineSecondary>
                            ) : null}
                        </>
                    }
                />
            </div>
        </div>
    );
};

export default MyPortfolioSummaryCards;
